<script setup lang="ts">
import {
    onMounted, ref, watch,
} from 'vue';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import {
    type FlightSchoolCourseStudent, printableStudentFromCourseStudent, type PrintableCourseStudent,
} from '@/types/FlightSchoolStudent';
import StudentLinkTable from '@/components/Reports/StudentLinkTable.vue';
import type FlightSchool from '@/types/FlightSchool';
import { dateSortCriteria, numericSortCriteria } from '@/functions/sortCriteria';
import StudentRepository from '@/repositories/StudentRepository';

const props = defineProps<{
    currentSchool: FlightSchool,
    currentCourse: FlightSchoolCourse,
    currentRatingUuid: string,
}>();

type EnrollmentRow = PrintableCourseStudent & {
    isFocused: boolean;
};

// table should be in the loading state until the data is loaded
const loading = ref<boolean>(true);

const students = ref<FlightSchoolCourseStudent[]>([]);
const studentRows = ref<EnrollmentRow[]>([]);
const loadCourseStudents = async () => {
    students.value = await StudentRepository.getCourseStudentsByRatingUuid(
        props.currentSchool,
        props.currentCourse,
        props.currentRatingUuid,
    );

    studentRows.value = students.value.map(
        (student: FlightSchoolCourseStudent): EnrollmentRow => ({
            ...printableStudentFromCourseStudent(student),
            isFocused: false,
        }),
    );
};

const columns = [
    {
        name: 'id', label: 'ID', field: 'id', sortable: true, align: 'left' as const,
    },
    {
        name: 'name', label: 'Name', field: 'name', sortable: true, align: 'left' as const,
    },
    {
        name: 'email', label: 'Email', field: 'email', sortable: true, align: 'left' as const,
    },
    {
        name: 'enrollDate', label: 'Enrollment Date', field: 'enrollDateString', sortable: true, sort: dateSortCriteria, align: 'left' as const,
    },
    {
        name: 'lastAccess', label: 'Last Access', field: 'lastAccessString', sortable: true, sort: dateSortCriteria, align: 'left' as const,
    },
    {
        name: 'courseStatus', label: 'Course Status', field: 'courseStatus', sortable: true, align: 'left' as const,
    },
    {
        name: 'gradDate', label: 'Graduation Date', field: 'gradDateString', sortable: true, sort: dateSortCriteria, align: 'left' as const,
    },
    {
        name: 'examStatus', label: 'Exam Status', field: 'examStatus', sortable: true, align: 'left' as const,
    },
    {
        name: 'examDate', label: 'Exam Date', field: 'examDateString', sortable: true, sort: dateSortCriteria, align: 'left' as const,
    },
    {
        name: 'faaTestGrades', label: 'FAA Test Grades', field: 'faaTestGradesString', sortable: true, sort: numericSortCriteria, align: 'left' as const,
    },
];

/**
 * Request the data from the server when the component is mounted
 */
onMounted(async () => {
    await loadCourseStudents();
    loading.value = false;
});

/**
 * watch for changes to the current school and course
 * when either changes, we need to reload the data
 */
watch(() => [props.currentSchool, props.currentCourse, props.currentRatingUuid], async () => {
    loading.value = true;
    // check admin access to school and course here?
    studentRows.value = [];
    await loadCourseStudents();
    loading.value = false;
});
</script>

<template>
    <student-link-table
        :rows="studentRows"
        :columns="columns"
        :loading="loading"
        :sort-by="columns[0].name"
        :enable-date-filter="true"
    />
</template>
